import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, User, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
import styles from './ChatBot.module.css';

interface Message {
    text: string;
    isBot: boolean;
    timestamp: string;
}

export const ChatBot: React.FC = () => {
    const [messages, setMessages] = useState<Message[]>(() => {
        // Load messages from localStorage on initial render
        const savedMessages = localStorage.getItem('chatMessages');
        return savedMessages ? JSON.parse(savedMessages) : [];
    });
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [serverStatus, setServerStatus] = useState<'online' | 'offline'>('online');
    const [showPopMessage, setShowPopMessage] = useState(false);
    const [messageCount, setMessageCount] = useState(() => {
        // Load message count from localStorage
        const savedCount = localStorage.getItem('messageCount');
        return savedCount ? parseInt(savedCount, 10) : 0;
    });
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isMuted, setIsMuted] = useState(() => {
        const savedMuted = localStorage.getItem('isMuted');
        return savedMuted ? JSON.parse(savedMuted) : false;
    });
    const [lastBotMessage, setLastBotMessage] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Save messages to localStorage whenever they change
    useEffect(() => {
        localStorage.setItem('chatMessages', JSON.stringify(messages));
    }, [messages]);

    // Save message count to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('messageCount', messageCount.toString());
    }, [messageCount]);

    // Track last bot message
    useEffect(() => {
        // Update last bot message whenever messages change
        const lastBot = [...messages].reverse().find(m => m.isBot);
        if (lastBot) {
            setLastBotMessage(lastBot.text);
        }
    }, [messages]);

    // Save mute preference
    useEffect(() => {
        localStorage.setItem('isMuted', JSON.stringify(isMuted));
        if (!isMuted && lastBotMessage) {
            speak(lastBotMessage);
        }
    }, [isMuted, lastBotMessage]);

    const getBritishFemaleVoice = () => {
        const voices = window.speechSynthesis.getVoices();

        // Try to find a British female voice
        const britishFemaleVoice = voices.find(voice =>
            voice.lang.includes('en-GB') &&
            (voice.name.toLowerCase().includes('female') ||
                voice.name.toLowerCase().includes('fiona') ||
                voice.name.toLowerCase().includes('amy') ||
                voice.name.toLowerCase().includes('emma') ||
                voice.name.toLowerCase().includes('alice') ||
                voice.name.toLowerCase().includes('clara'))
        );

        // Fallback to any British voice
        const britishVoice = voices.find(voice => voice.lang.includes('en-GB'));

        // Final fallback to any English female voice
        const englishFemaleVoice = voices.find(voice =>
            voice.lang.includes('en') &&
            (voice.name.toLowerCase().includes('female') ||
                voice.name.toLowerCase().includes('samantha') ||
                voice.name.toLowerCase().includes('victoria') ||
                voice.name.toLowerCase().includes('karen'))
        );

        return britishFemaleVoice || britishVoice || englishFemaleVoice || voices[0];
    };

    const speak = (text: string) => {
        if (isMuted) return;

        // Cancel any ongoing speech
        window.speechSynthesis.cancel();

        const utterance = new SpeechSynthesisUtterance(text);

        // Get the appropriate voice
        utterance.voice = getBritishFemaleVoice();
        utterance.pitch = 1;
        utterance.rate = 0.9; // Slightly slower for better clarity
        utterance.volume = 1;

        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => {
            setIsSpeaking(false);
        };
        utterance.onerror = () => {
            setIsSpeaking(false);
        };

        window.speechSynthesis.speak(utterance);
    };

    // Initialize voices
    useEffect(() => {
        const loadVoices = () => {
            window.speechSynthesis.getVoices();
        };

        loadVoices();
        window.speechSynthesis.onvoiceschanged = loadVoices;

        return () => {
            window.speechSynthesis.onvoiceschanged = null;
        };
    }, []);

    // Clean up speech synthesis when component unmounts
    useEffect(() => {
        return () => {
            window.speechSynthesis.cancel();
        };
    }, []);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && !isLoading) {
            inputRef.current?.focus();
        }
    }, [isOpen, isLoading, messages]);

    // Check server status periodically
    useEffect(() => {
        const checkServerStatus = async () => {
            try {
                const response = await fetch('/api/chat', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ message: 'ping' })
                });
                setServerStatus(response.ok ? 'online' : 'offline');
            } catch (error) {
                setServerStatus('offline');
            }
        };

        // Check immediately and then every 30 seconds
        checkServerStatus();
        const interval = setInterval(checkServerStatus, 30000);
        return () => clearInterval(interval);
    }, []);

    // Show initial message with typing indicator
    useEffect(() => {
        const showInitialMessage = async () => {
            setIsLoading(true);
            await new Promise(resolve => setTimeout(resolve, 1500));
            setMessages([{
                text: "Hi! I'm Smile, your cybersecurity assistant. How can I help you today?",
                isBot: true,
                timestamp: new Date().toISOString()
            }]);
            setIsLoading(false);
        };
        showInitialMessage();
    }, []);

    // Show initial pop message after a delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setShowPopMessage(true);
            // Hide pop message after 5 seconds
            setTimeout(() => setShowPopMessage(false), 5000);
        }, 2000); // Show after 2 seconds

        return () => clearTimeout(timer);
    }, []);

    // Listen for external open event
    useEffect(() => {
        const handleOpen = () => {
            setIsOpen(true);
            setShowPopMessage(false);
        };
        window.addEventListener('open-smile-chat', handleOpen);
        return () => window.removeEventListener('open-smile-chat', handleOpen);
    }, []);

    const addMessageWithTypingIndicator = async (message: Message) => {
        setIsLoading(true);
        await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 500)); // Random delay between 1-1.5s
        setMessages(prev => [...prev, message]);
        if (message.isBot && !isMuted) {
            speak(message.text);
        }
        setIsLoading(false);
    };

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userMessage: Message = {
            text: input.trim(),
            isBot: false,
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const response = await fetch('/api/chat', {
                method: 'POST',
                body: JSON.stringify({
                    message: input.trim(),
                    messageCount: messageCount,
                    conversationHistory: messages.slice(-5) // Send last 5 messages for context
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            // Handle redirect case
            if (data.shouldRedirect) {
                await addMessageWithTypingIndicator({
                    text: data.response,
                    isBot: true,
                    timestamp: new Date().toISOString()
                });
                setMessageCount(0); // Reset count
                localStorage.removeItem('chatMessages'); // Clear chat history
                localStorage.removeItem('messageCount'); // Clear message count
                return;
            }

            await addMessageWithTypingIndicator({
                text: data.response,
                isBot: true,
                timestamp: new Date().toISOString()
            });
            setMessageCount(data.messageCount);
            setServerStatus('online');
        } catch (error) {
            console.error('Error:', error);
            setServerStatus('offline');
            await addMessageWithTypingIndicator({
                text: "I apologize, but I'm having trouble connecting to the server. Please try again later. ",
                isBot: true,
                timestamp: new Date().toISOString()
            });
        } finally {
            setIsLoading(false);
        }
    };

    // Clear chat history function
    const clearChat = () => {
        setMessages([]);
        setMessageCount(0);
        localStorage.removeItem('chatMessages');
        localStorage.removeItem('messageCount');
    };

    const handleMuteToggle = () => {
        setIsMuted((prev: boolean) => {
            const newMuted = !prev;
            if (!newMuted && lastBotMessage) {
                // When unmuting, read the last bot message
                setTimeout(() => {
                    speak(lastBotMessage);
                }, 100);
            } else if (newMuted) {
                window.speechSynthesis.cancel(); // Stop speaking when muted
            }
            return newMuted;
        });
    };

    const welcomeMessage = "Hi! I'm Smile, your cybersecurity assistant. How can I help you today?";

    const handleChatToggle = () => {
        const newIsOpen = !isOpen;
        setIsOpen(newIsOpen);
        if (!newIsOpen) {
            window.speechSynthesis.cancel(); // Stop speech when closing
        } else if (!isMuted) {
            // Use the click interaction to trigger speech
            setTimeout(() => {
                speak(welcomeMessage);
            }, 300); // Small delay for smooth animation
        }
        setShowPopMessage(false);
    };

    useEffect(() => {
        if (isOpen) {
            // Initialize speech synthesis on chat open
            window.speechSynthesis.getVoices();
        }
    }, [isOpen]);

    return (
        <div className="fixed right-0 bottom-0 z-50">
            {/* Initial Pop Message */}
            <div className={`fixed right-4 bottom-28 transform transition-all duration-500 ${showPopMessage
                ? 'scale-100 opacity-100 translate-y-0'
                : 'scale-95 opacity-0 translate-y-4 pointer-events-none'
                }`}>
                <div className="relative flex items-end space-x-2">
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-primary/50 relative mb-1">
                        <video src="/give_hand_wave_action_to_the_r.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover scale-[1.5]" />
                    </div>
                    <div className="relative bg-[#1c1c1f] p-4 rounded-2xl shadow-xl max-w-xs border border-white/10">
                        <div className="flex flex-col space-y-2">
                            <p className="text-white/90 text-sm">
                                Hi! I'm Smile AI. Need help with cybersecurity? Click here to chat with me! 👋
                            </p>
                            <button
                                onClick={() => {
                                    setIsOpen(true);
                                    setShowPopMessage(false);
                                }}
                                className="text-xs text-primary hover:text-primary/80 transition-colors text-left font-medium"
                            >
                                Open Chat →
                            </button>
                        </div>
                        {/* Arrow pointing to chat button */}
                        <div className="absolute -bottom-2 right-8 w-4 h-4 bg-[#1c1c1f] border-r border-b border-white/10 transform rotate-45 translate-y-1"></div>
                    </div>
                </div>
            </div>

            {/* Chat Toggle Button */}
            <button
                onClick={handleChatToggle}
                aria-label={isOpen ? "Close chat" : "Open chat with Smile"}
                title={isOpen ? "Close chat" : "Open chat with Smile"}
                className={`fixed right-6 bottom-6 z-50 flex items-center justify-center transition-all duration-300 ${
                    isOpen 
                        ? 'w-14 h-14 bg-primary text-[#0b1121] rounded-full shadow-lg hover:scale-105 hover:bg-primary/90' 
                        : 'p-1 bg-[#0b1121] rounded-full shadow-[0_0_15px_rgba(239,174,0,0.4)] hover:shadow-[0_0_25px_rgba(239,174,0,0.6)] hover:scale-105 border-2 border-primary'
                }`}
            >
                {isOpen ? (
                    <X size={24} />
                ) : (
                    <div className="w-12 h-12 rounded-full overflow-hidden relative">
                        <video 
                            src="/give_hand_wave_action_to_the_r.mp4" 
                            autoPlay 
                            loop 
                            playsInline 
                            muted
                            className="absolute inset-0 w-full h-full object-cover scale-[1.5]"
                        />
                    </div>
                )}
            </button>

            {/* Chat Window */}
            <div className={`fixed md:right-6 md:bottom-24 md:w-[400px] md:h-[600px] w-full h-full top-0 left-0 md:top-auto md:left-auto bg-[#0b1121] shadow-2xl md:rounded-[24px] flex flex-col transform transition-all duration-300 border border-white/5 overflow-hidden ${isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4 pointer-events-none'
                }`}>
                
                {/* Header */}
                <div className="bg-[#0b1121] p-4 flex flex-col space-y-3 border-b border-white/5 z-10">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="relative w-11 h-11 rounded-full overflow-hidden border border-primary/50 bg-black">
                                <video 
                                    src="/give_hand_wave_action_to_the_r.mp4" 
                                    autoPlay 
                                    loop 
                                    playsInline 
                                    muted
                                    className="absolute inset-0 w-full h-full object-cover scale-[1.5]"
                                />
                                <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-[#0b1121]"></div>
                            </div>
                            <div>
                                <h3 className="text-white font-bold text-base leading-tight">Smile AI</h3>
                                <p className="text-xs text-white/50">Expectation Walkers Assistant</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-2">
                            {/* Mute/Unmute button */}
                            <button
                                onClick={handleMuteToggle}
                                className={`text-primary border border-primary/30 hover:bg-primary/10 transition-colors duration-200 p-2 rounded-xl text-sm ${isSpeaking ? 'animate-pulse' : ''}`}
                                title={isMuted ? "Unmute voice" : "Mute voice"}
                            >
                                {isMuted ? '🔇' : '🔊'}
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center justify-center">
                        <span className="text-[10px] text-white/40 flex items-center gap-1">
                            🔒 Support sessions auto-close after 5 min of inactivity
                        </span>
                    </div>
                </div>

                {/* Messages */}
                <div className={`flex-1 overflow-y-auto relative z-10 chat-scrollbar bg-[#0b1121]`}>
                    <div className="p-4 space-y-6">
                        
                        {/* Large Avatar at Top */}
                        <div className="flex justify-center mt-2 mb-8">
                            <div className="w-28 h-28 rounded-full overflow-hidden border border-white/10 shadow-[0_0_40px_rgba(239,174,0,0.05)] relative bg-black">
                                <video 
                                    src="/give_hand_wave_action_to_the_r.mp4" 
                                    autoPlay 
                                    loop 
                                    playsInline 
                                    muted
                                    className="absolute inset-0 w-full h-full object-cover scale-[1.2]"
                                />
                            </div>
                        </div>

                        {messages.map((message, index) => {
                            const isUser = !message.isBot;
                            return (
                                <motion.div
                                    key={index}
                                    className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} relative z-20 space-x-2 items-end`}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3 }}
                                >
                                    {message.isBot && (
                                        <div className="w-7 h-7 rounded-full overflow-hidden border border-white/5 flex-shrink-0 mb-1 relative bg-black">
                                            <video src="/give_hand_wave_action_to_the_r.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover scale-[1.5]" />
                                        </div>
                                    )}
                                    <motion.div 
                                        className={`max-w-[85%] p-3.5 rounded-2xl ${
                                            isUser 
                                                ? 'bg-primary text-[#0b1121] rounded-br-sm' 
                                                : 'bg-[#1c1c1f] text-white/90 rounded-bl-sm border border-white/5'
                                        }`}
                                    >
                                        <p className="text-sm font-medium">{message.text}</p>
                                    </motion.div>
                                </motion.div>
                            );
                        })}
                        {isLoading && (
                            <motion.div 
                                className="flex justify-start items-end space-x-2 relative z-20"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <div className="w-7 h-7 rounded-full overflow-hidden border border-white/5 flex-shrink-0 mb-1 relative bg-black">
                                    <video src="/give_hand_wave_action_to_the_r.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover scale-[1.5]" />
                                </div>
                                <div className="bg-[#1c1c1f] text-white/90 rounded-2xl rounded-bl-sm border border-white/5 p-3.5 px-4">
                                    <div className="flex space-x-1.5 items-center h-4">
                                        <motion.div animate={{ y: [0, -3, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0 }} className="w-1.5 h-1.5 bg-white/50 rounded-full"></motion.div>
                                        <motion.div animate={{ y: [0, -3, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }} className="w-1.5 h-1.5 bg-white/50 rounded-full"></motion.div>
                                        <motion.div animate={{ y: [0, -3, 0] }} transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }} className="w-1.5 h-1.5 bg-white/50 rounded-full"></motion.div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                        {messageCount >= 19 && !isLoading && (
                            <div className="text-center relative z-20">
                                <span className="text-xs text-primary/80 animate-pulse">
                                    ⚠️ One message remaining before redirecting to website
                                </span>
                            </div>
                        )}
                        <div ref={messagesEndRef} className="relative z-20" />
                    </div>
                </div>

                {/* Input Area */}
                <div className="p-4 bg-[#0b1121] relative z-10 border-t border-white/5">
                    <div className="flex items-center space-x-3 max-w-full">
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                            placeholder="Ask Smile AI anything..."
                            className="flex-1 p-3.5 px-5 bg-[#1c1c1f] text-white rounded-full border border-white/5 focus:outline-none focus:border-white/10 placeholder-white/40 text-sm w-full transition-colors"
                            disabled={isLoading}
                        />
                        <motion.button
                            whileHover={!isLoading ? { scale: 1.05 } : {}}
                            whileTap={!isLoading ? { scale: 0.95 } : {}}
                            onClick={handleSend}
                            disabled={isLoading}
                            aria-label="Send message"
                            title="Send message"
                            className={`p-3.5 bg-primary text-[#0b1121] rounded-full transition-all duration-300 shadow-lg ${isLoading
                                ? 'opacity-50 cursor-not-allowed'
                                : 'hover:bg-primary/90'
                                }`}
                        >
                            <Send size={18} />
                        </motion.button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatBot;
